import { ReactNode } from 'react';
import { Pressable, StyleSheet } from 'react-native';

import ChevronRightIcon from '@/assets/icons/chevronRightNew';
import { EventTime } from '@/components/EventTime';
import { LineSeparator } from '@/components/LineSeparator';
import { useLiveEventStatus } from '@/components/LiveEventStatus';
import { SgpIcon } from '@/components/SgpIcon';
import { Text } from '@/components/TextComponent';
import { Box, Column, Row } from '@/components/lib/components';
import { Event } from '@/feature/event-details-sbk/types';
import { useIsSgpEnabled } from '@/hooks/use-is-sgp-enabled';
import { TeamOrder } from '@/utils/sport-utils';

import { ds } from '../lib/design.system';
import { MarketColumns } from './parts/MarketColumns';
import { useIsMatchupLive } from './use-is-matchup-live';
import { MarketColumn as MarketColumnType } from './utils';

type MatchupCardLayoutProps = {
    event: Event;
    marketColumns: MarketColumnType[];
    totalRows: number;
    teamOrder: TeamOrder;
    showHeader?: boolean;
    isLastItem?: boolean;
    onPress?: () => void;
    children: ReactNode;
};

// Shared layout for dynamic matchup cards: outer spacing, market columns, status + chevron bar,
// and the line separator. Variant-specific player rows are passed in as children.
export const MatchupCardLayout = ({
    event,
    marketColumns,
    totalRows,
    teamOrder,
    showHeader = false,
    isLastItem = false,
    onPress,
    children,
}: MatchupCardLayoutProps) => {
    const isSgpEnabled = useIsSgpEnabled(event.is_sgp_enabled);
    const isLive = useIsMatchupLive(event);
    const liveEventStatus = useLiveEventStatus(event);

    return (
        <Box gap="s16" pb="s20" mx="s16">
            <Row justifyContent="space-between" alignItems="flex-end" gap="s16">
                {/** Team Name Columns */}
                <Column flex={1}>
                    <Pressable onPress={onPress}>
                        <Column gap="s3" flex={1}>
                            {children}
                        </Column>
                    </Pressable>
                </Column>
                {/** Market Columns */}
                <MarketColumns
                    event={event}
                    marketColumns={marketColumns}
                    totalRows={totalRows}
                    teamOrder={teamOrder}
                    showHeader={showHeader}
                />
            </Row>
            <Pressable onPress={onPress}>
                <Row gap="s12" alignItems="center">
                    {isSgpEnabled ? <SgpIcon /> : null}
                    <Row flex={1} justifyContent="space-between" alignItems="center">
                        <Row gap="s3">
                            <EventTime
                                variant={isLive ? 'labelLarge' : 'bodySmall'}
                                color={isLive ? ds.colorToken.text.danger : 'neutral300'}
                                event={event}
                                numberOfLines={1}
                            />
                            {liveEventStatus ? (
                                <>
                                    <Text variant={'bodySmall'} color="neutral300">
                                        •
                                    </Text>
                                    <Text variant={'bodySmall'} color="neutral300" numberOfLines={1}>
                                        {liveEventStatus}
                                    </Text>
                                </>
                            ) : null}
                        </Row>
                        <ChevronRightIcon />
                    </Row>
                </Row>
            </Pressable>
            {!isLastItem ? <LineSeparator style={styles.lineSeparator} /> : null}
        </Box>
    );
};

const styles = StyleSheet.create({
    lineSeparator: {
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: 0,
    },
});
